import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { auth, db, isDBSEmail, isTeacherEmail } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, updateDoc, increment } from 'firebase/firestore';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = async (uid) => {
    try {
      const docRef = doc(db, 'users', uid);
      const snap = await getDoc(docRef);
      if (snap.exists()) setProfile(snap.data());
    } catch (err) { console.error('Profile fetch error:', err); }
  };

  // Weekly rewards check
  const checkWeeklyRewards = async (userId, userData) => {
    try {
      const now = new Date();
      const currentWeek = getWeekNumber(now);
      const lastWeek = userData.lastWeeklyRewardWeek || 0;
      
      if (currentWeek > lastWeek) {
        await updateDoc(doc(db, 'users', userId), {
          points: increment(2),
          lastWeeklyRewardWeek: currentWeek,
          lastWeeklyRewardDate: serverTimestamp()
        });
        console.log('+2 weekly points');
        return true;
      }
      return false;
    } catch (err) {
      console.error('Weekly reward error:', err);
      return false;
    }
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        if (!isDBSEmail(firebaseUser.email)) {
          await signOut(auth);
          setError('Only @dbs.edu.hk emails allowed');
          setUser(null);
        } else {
          setUser(firebaseUser);
          await fetchProfile(firebaseUser.uid);
          // Check weekly rewards
          const userRef = doc(db, 'users', firebaseUser.uid);
          const snap = await getDoc(userRef);
          if (snap.exists()) {
            await checkWeeklyRewards(firebaseUser.uid, snap.data());
          }
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signUp = async (email, password, displayName) => {
    if (!isDBSEmail(email)) throw new Error('Use your @dbs.edu.hk email');
    const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', newUser.uid), {
      uid: newUser.uid,
      email: newUser.email,
      displayName: displayName || email.split('@')[0],
      grade: '',
      subjectsGoodAt: [],
      subjectsNeedHelp: [],
      contactWhatsApp: '',
      contactEmail: '',
      points: 10,
      rating: 0,
      ratingCount: 0,
      isTeacher: isTeacherEmail(newUser.email),
      createdAt: serverTimestamp(),
      lastActive: serverTimestamp(),
      lastWeeklyRewardWeek: 0,
      lastWeeklyRewardDate: null
    });
    return newUser;
  };

  const signIn = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const logOut = () => signOut(auth);
  
  const updateProfile = async (updates) => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { ...updates, lastActive: serverTimestamp() });
    await fetchProfile(user.uid);
  };

  const value = useMemo(() => ({
    user, profile, loading, error, signUp, signIn, logOut, updateProfile,
    isTeacher: profile?.isTeacher === true
  }), [user, profile, loading, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
